import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { type Decision, decide, getCard } from "../../api/client";
import { type CardDetail, CardStatus, type Images } from "../../api/models";
import { StatusBadge, VerdictBadge } from "../../components/Badge";
import { CatalogThumb } from "../../components/CatalogThumb";
import { ZoomImage } from "../../components/Lightbox";
import { PriceLine } from "../../components/PriceLine";
import { productUrl } from "../../lib/catalog";
import { errorMessage, formatDate, showValue } from "../../lib/format";
import { buildCandidates, type Candidate, defaultSource, defaultSport, SPORTS, searchCandidate } from "./candidates";
import { EbayPanel } from "./EbayPanel";
import { RunsPanel } from "./RunsPanel";
import { SearchBox } from "./SearchBox";
import { useNeighbours } from "./useNeighbours";

const typing = (e: KeyboardEvent) =>
  e.target instanceof HTMLElement && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);

export function ReviewPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const statusParam = CardStatus.safeParse(params.get("status"));
  const status = statusParam.success ? statusParam.data : undefined;
  const { prev, next } = useNeighbours(id, status);

  const [card, setCard] = useState<CardDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extra, setExtra] = useState<Candidate[]>([]);
  const [images, setImages] = useState<Images>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [sport, setSport] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    getCard(id)
      .then((c) => {
        setCard(c);
        setExtra([]);
        setImages(c.images);
        setSport(defaultSport(c));
        setNote(c.note ?? "");
        const base = buildCandidates(c);
        setSelected((base.find((x) => x.id === c.productId) ?? base[0]).key);
      })
      .catch((e) => setError(errorMessage(e)));
  }, [id]);
  useEffect(load, [load]);

  const candidates = useMemo(() => (card ? [...buildCandidates(card), ...extra] : []), [card, extra]);
  const current = candidates.find((c) => c.key === selected) ?? null;
  const approved = card?.status === "APPROVED" && !!current && current.id === card.productId;
  const rejected = card?.status === "REJECTED";
  const noProduct = card?.status === "NO_PRODUCT";
  const goTo = useCallback(
    (target: number | null) => {
      if (target !== null) navigate(`/cards/${target}${status ? `?status=${status}` : ""}`);
    },
    [navigate, status],
  );

  const save = useCallback(
    async (body: Decision) => {
      if (!card || saving) return;
      setSaving(true);
      setSaveError(null);
      try {
        await decide(card.id, body);
        const fresh = await getCard(card.id);
        setCard(fresh);
        setImages((prevImages) => ({ ...prevImages, ...fresh.images }));
      } catch (e) {
        setSaveError(errorMessage(e));
      } finally {
        setSaving(false);
      }
    },
    [card, saving],
  );

  const approve = useCallback(() => {
    if (!current || approved) return;
    return save({
      status: "APPROVED",
      productId: current.id,
      identity: current.identity,
      sport: sport || null,
      note: note || null,
    });
  }, [current, approved, save, sport, note]);
  const reject = useCallback(() => {
    if (rejected) return;
    return save({ status: "REJECTED", note: note || null });
  }, [rejected, save, note]);
  // A fine scan the catalog has no product for: production's reading stands as the identity.
  const markNoProduct = useCallback(() => {
    if (!card || noProduct) return;
    return save({
      status: "NO_PRODUCT",
      identity: card.productionIdentity as Record<string, unknown> | null,
      sport: sport || null,
      note: note || null,
    });
  }, [card, noProduct, save, sport, note]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (typing(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "a") approve();
      else if (e.key === "n") markNoProduct();
      else if (e.key === "r") reject();
      else if (e.key === "j") goTo(next);
      else if (e.key === "k") goTo(prev);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [approve, markNoProduct, reject, goTo, next, prev]);

  if (error) return <p className="error">{error}</p>;
  if (!card) return <p className="muted">Loading…</p>;

  const sportOptions = sport && !SPORTS.includes(sport) ? [sport, ...SPORTS] : SPORTS;
  const identity = card.productionIdentity ?? {};

  return (
    <>
      <div className="review-head">
        <Link to={`/${status ? `?status=${status}` : ""}`}>‹ Cards</Link>
        <h1 style={{ margin: 0 }}>
          Scan <span className="mono">{card.scanId}</span>
        </h1>
        <a href={card.scanUrl} target="_blank" rel="noreferrer" className="muted">
          hobbyscan ↗
        </a>
        <StatusBadge status={card.status} />
        {card.reviewedBy && (
          <span className="muted">
            {card.reviewedBy} · {formatDate(card.reviewedAt)}
          </span>
        )}
        <span className="muted">scanned {formatDate(card.scannedAt)}</span>
        <span className="spacer" />
        <button type="button" disabled={prev === null} onClick={() => goTo(prev)}>
          ‹ Prev <span className="kbd">k</span>
        </button>
        <button type="button" disabled={next === null} onClick={() => goTo(next)}>
          Next <span className="kbd">j</span> ›
        </button>
      </div>

      <div className="review">
        <div>
          <ZoomImage className="photo" src={card.photoUrl} alt="scan" />
        </div>
        <div>
          <div className="candidates">
            {candidates.map((c) => (
              <label key={c.key} className={`candidate${c.key === selected ? " selected" : ""}`}>
                <span className="label">
                  {c.label} {c.verdict && <VerdictBadge verdict={c.verdict} />}
                </span>
                <CatalogThumb url={images[c.id]} alt={c.name} beside={card.photoUrl} />
                <span className="name">{c.name}</span>
                <span className="console">
                  {c.console || "—"} ·{" "}
                  <a href={productUrl(c.id, c.cardType)} target="_blank" rel="noreferrer" className="mono">
                    #{c.id}
                  </a>
                </span>
                <PriceLine prices={c.prices} />
                <span>
                  <input
                    type="radio"
                    name="candidate"
                    checked={c.key === selected}
                    onChange={() => setSelected(c.key)}
                  />
                  this is the card
                </span>
              </label>
            ))}
          </div>

          <EbayPanel card={card} />

          <SearchBox
            key={card.id}
            initialSource={defaultSource(card)}
            initialQuery={card.priceQuery}
            onPick={(p, source, found) => {
              const cand = searchCandidate(p, source);
              setImages((prevImages) => ({ ...prevImages, ...found }));
              setExtra((prevExtra) => (prevExtra.some((x) => x.id === p.id) ? prevExtra : [...prevExtra, cand]));
              setSelected(candidates.find((x) => x.id === p.id)?.key ?? cand.key);
            }}
          />

          <div className="panel decision">
            <label className="field">
              Sport
              <select value={sport} onChange={(e) => setSport(e.target.value)}>
                <option value="">none</option>
                {sportOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <input type="text" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} />
            <button
              type="button"
              className={`ok${approved ? " active" : ""}`}
              aria-pressed={approved}
              disabled={saving || !current || approved}
              onClick={approve}
            >
              {approved ? "✓ Approved" : "Approve"} <span className="kbd">a</span>
            </button>
            <button
              type="button"
              className={`warn${noProduct ? " active" : ""}`}
              aria-pressed={noProduct}
              disabled={saving || noProduct}
              onClick={markNoProduct}
              title="A usable scan, but the catalog has no product for it: the engine should price nothing"
            >
              {noProduct ? "✓ Not in catalog" : "Not in catalog"} <span className="kbd">n</span>
            </button>
            <button
              type="button"
              className={`danger${rejected ? " active" : ""}`}
              aria-pressed={rejected}
              disabled={saving || rejected}
              onClick={reject}
            >
              {rejected ? "✗ Rejected" : "Reject"} <span className="kbd">r</span>
            </button>
            {saving && <span className="muted">Saving…</span>}
            {saveError && <span className="error">{saveError}</span>}
          </div>

          <div className="panel">
            <h3>Production reading</h3>
            {Object.keys(identity).length === 0 ? (
              <span className="muted">No normalized identity.</span>
            ) : (
              <dl className="kv">
                {Object.entries(identity).map(([k, v]) => (
                  <div key={k} style={{ display: "contents" }}>
                    <dt>{k}</dt>
                    <dd>{showValue(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="panel">
            <h3>Runs</h3>
            <RunsPanel card={card} />
          </div>
        </div>
      </div>
    </>
  );
}
